/**
 * Open-from-pin path for `.sql` (query) FILE-backed workspace pins. Split out of
 * `openFilePin.ts` (K8) — see that module's header for why file pins use a
 * dedicated path (sourcePin-only dedup, explicit envelope binding, on-focus
 * disk revalidation) instead of the dedupe-by-path script helpers.
 */
import {
  getFileNameFromPath,
  hashText,
  isFileOutOfDate,
} from '../../../workspace/query-editor/utils/queryFileUtils';
import { openSqlFile } from '../../../workspace/query-editor/services/queryFilesApi';
import { isQueryTab } from '../../../workspace/core/types/workspaceTypes';
import type { Tab } from '../../../workspace/core/types/workspaceTypes';
import type { PinnedWorkspaceRef } from '../../../../types/electron';
import { useWorkspaceStore } from '../../../workspace/core/store/workspaceStore';
import {
  connectionInfoFor,
  findLinkedTabs,
  mostRecentlyActive,
  type WorkspaceState,
} from './openWorkspacePin';
import { showFileOpenError, warnUnsavedConflict, type FilePinOpenMode } from './openFilePin';

export async function openSqlFilePin({
  connectionId,
  ref,
  filePath,
}: {
  connectionId: string;
  ref: PinnedWorkspaceRef;
  filePath: string;
}): Promise<FilePinOpenMode> {
  const result = await openSqlFile({ filePath });
  if (!result.success) {
    showFileOpenError(filePath, result.error);
    return null;
  }

  const query = result.query ?? '';
  const freshHash = hashText(query);
  const resolvedPath = result.filePath ?? filePath;

  // Re-read workspace state AFTER the disk read (K3) — never act on a pre-await
  // snapshot: the linked set may have vanished (tab closed) or been created.
  const ws = useWorkspaceStore.getState();
  const linked = findLinkedTabs(connectionId, ref.id, ws);

  if (linked.length > 0) {
    revalidateQueryTab(mostRecentlyActive(linked, ws), query, freshHash, resolvedPath, ws);
    return 'focus';
  }

  const tabId = ws.addTab({
    type: 'query',
    title: getFileNameFromPath(resolvedPath),
    content: { query, filePath: resolvedPath, fileLastSavedHash: freshHash },
    // Explicit envelope binding (G3) — never inferred from script association.
    connectionInfo: connectionInfoFor(connectionId, ref.database),
  });
  ws.setSourcePin(tabId, { connectionId, pinId: ref.id });
  return 'new';
}

function revalidateQueryTab(
  tab: Tab,
  freshQuery: string,
  freshHash: string,
  filePath: string,
  ws: WorkspaceState,
): void {
  ws.setActiveTab(tab.id);
  if (!isQueryTab(tab)) return;

  const { query, fileLastSavedHash } = tab.content;
  if (fileLastSavedHash === freshHash) return; // disk unchanged since last load

  // Dirty = in-memory text diverges from the last-saved hash (unsaved edits).
  if (isFileOutOfDate({ query, fileLastSavedHash })) {
    warnUnsavedConflict(filePath);
    return;
  }
  ws.updateTabContent(tab.id, { query: freshQuery, fileLastSavedHash: freshHash });
}
